import { TypedDocumentNode, gql, useQuery } from '@apollo/client';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import { useCallback, useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BLOG } from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Excerpt } from 'components/Excerpt';
import { BLOG_CATEGORY, BLOG_ROOT, BlogSport } from 'constants/routes';
import { getPostCategory } from 'lib/postCategory';

import {
  getAllCategories,
  getAllCategoriesVariables,
  getAllPosts,
  getAllPostsVariables,
} from './__generated__/page.graphql';

const GET_ALL_CATEGORIES = gql`
  query getAllCategories {
    categories(
      first: 100
      where: { hideEmpty: true, hierarchical: true, parent: 0 }
    ) {
      nodes {
        slug
        name
        children {
          nodes {
            slug
            name
            count
          }
        }
      }
    }
  }
` as TypedDocumentNode<getAllCategories, getAllCategoriesVariables>;

const GET_ALL_POSTS = gql`
  query getAllPosts(
    $first: Int
    $last: Int
    $after: String
    $before: String
    $categoryName: String
  ) {
    posts(
      first: $first
      last: $last
      after: $after
      before: $before
      where: { categoryName: $categoryName }
    ) {
      nodes {
        date
        featured {
          featured
        }
        ...getPostCategory_Post
        ...PostExcerpt
      }
      edges {
        cursor
      }
      pageInfo {
        hasPreviousPage
        hasNextPage
        endCursor
        startCursor
      }
    }
  }
  ${Excerpt.fragment}
  ${getPostCategory.fragments.post}
` as TypedDocumentNode<getAllPosts, getAllPostsVariables>;

const Root = styled.div`
  & a {
    text-decoration: none;
    margin-bottom: var(--half-unit);
  }
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5em;

  @media (${tabletAndAbove}) {
    display: grid;
    grid-template-columns: [main-start] 1fr 1fr 1fr [main-end];
  }

  p {
    text-align: justify;
    hyphens: auto;
  }

  figure > img {
    aspect-ratio: 1.8;
    object-fit: cover;
  }

  & > *:first-child {
    grid-column-start: main-start;
    grid-column-end: main-end;
    display: flex;
    flex-direction: column;
    gap: var(--unit);

    @media (${tabletAndAbove}) {
      & > div {
        display: inline-flex;
        min-width: 70%;
        width: min-content;
        flex-direction: column;
        background: var(--c-black);
        padding: var(--double-unit) var(--triple-unit);
        margin-top: -3em;

        & > h4 {
          font-size: 28px;
        }
      }
    }

    & > figure {
      margin: 0;
      > img {
        height: auto;
      }
    }
  }
`;

const StyledTabBar = styled(TabBar)`
  margin-bottom: var(--unit);
`;

const PaginationButtonContainer = styled.div`
  width: 100%;
  display: flex;
  gap: var(--triple-unit);
  justify-content: center;
  padding: var(--triple-unit) 0;
`;

const messages = defineMessages({
  seoTitle: {
    id: 'blog.seo.title',
    defaultMessage: 'Fantasy Sports Reimagined - Sorare Blog',
  },
  seoDescription: {
    id: 'blog.seo.description',
    defaultMessage:
      'Experience football, basketball, and baseball like never before. Collect. Play. Win. #OwnYourGame.',
  },
});

const removeWordFromString = (string: string | null, word: string) => {
  const regex = new RegExp(`\\b${word}\\b`, 'i');

  return string?.replace(regex, '').trim().replace(/\s+/g, ' ');
};

const ORDER: string[] = ['football', 'nba', 'mlb'] satisfies BlogSport[];

const categoryOrder = (c: { slug: string | null }) => {
  const i = ORDER.indexOf(c.slug ?? '');
  return i === -1 ? ORDER.length : i;
};

type Props = {
  sportCategory?: BlogSport;
};

const Home = ({ sportCategory }: Props = {}) => {
  const { category } = useParams();
  const navigate = useNavigate();
  const variables = {
    first: 13,
    last: null,
    after: null,
    before: null,
    categoryName: category ?? sportCategory,
  };

  const { data: categoriesData } = useQuery(GET_ALL_CATEGORIES);
  const { loading, data, fetchMore } = useQuery(GET_ALL_POSTS, { variables });
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const isTabletAndAbove = useIsTabletAndAbove();

  const rootCategories = categoriesData?.categories?.nodes
    ?.filter(c => ORDER.includes(c.slug!))
    ?.sort((a, b) => categoryOrder(a) - categoryOrder(b));

  const posts = useMemo(() => {
    if (!data?.posts) return null;
    const { nodes, ...rest } = data.posts;
    const sortedNodes = [...(nodes || [])].sort((a, b) => {
      if (a.featured?.featured === 'true') {
        if (b.featured?.featured !== 'true') return -1;
        return b.date!.localeCompare(a.date!);
      }
      if (b.featured?.featured === 'true') return 1;
      return b.date!.localeCompare(a.date!);
    });
    return {
      ...rest,
      nodes: sortedNodes,
    };
  }, [data]);

  const updateQuery: Parameters<
    typeof fetchMore<getAllPosts>
  >[0]['updateQuery'] = (previousResult, { fetchMoreResult }) => {
    return fetchMoreResult.posts?.edges.length
      ? fetchMoreResult
      : previousResult;
  };

  const selectCategory = useCallback(
    (c: string | undefined) => {
      navigate(`.${c ? `?category=${c}` : ''}`);
    },
    [navigate]
  );

  useEffect(() => {
    setPageMetadata(formatMessage(messages.seoTitle), {
      description: formatMessage(messages.seoDescription),
    });
  }, [formatMessage, setPageMetadata]);

  return (
    <Root>
      {!sportCategory ? (
        <StyledTabBar
          variant="flat"
          fullWidth={!isTabletAndAbove}
          transparent
          scrollable
          forceMobileLayout
        >
          <Tab value="all" onClick={() => selectCategory(undefined)}>
            All
          </Tab>
          {rootCategories?.map(cat => (
            <Tab
              key={cat.slug}
              value={cat.name || undefined}
              to={generatePath(BLOG_ROOT, { sport: cat.slug })}
            >
              {cat.name}
            </Tab>
          ))}
        </StyledTabBar>
      ) : (
        <>
          {categoriesData?.categories?.nodes && (
            <StyledTabBar
              variant="flat"
              transparent
              scrollable
              forceMobileLayout
            >
              <Tab to={BLOG}>Sports</Tab>
              {categoriesData?.categories?.nodes.flatMap(subCategory =>
                subCategory.children?.nodes
                  .filter(c => c.name?.toLowerCase().includes(sportCategory))
                  .map(c => (
                    <Tab
                      key={c.slug}
                      to={generatePath(BLOG_CATEGORY, {
                        sport: sportCategory,
                        category: c.slug,
                      })}
                    >
                      {removeWordFromString(c.name, sportCategory)}
                    </Tab>
                  ))
              )}
              {sportCategory === 'football' && (
                <Tab
                  to={generatePath(BLOG_CATEGORY, {
                    sport: sportCategory,
                    category: 'rivals',
                  })}
                >
                  Rivals
                </Tab>
              )}
            </StyledTabBar>
          )}
        </>
      )}

      {data?.posts && !loading ? (
        <>
          <Grid>
            {posts && posts.nodes.length === 0
              ? null
              : posts?.nodes.map((post, index) => (
                  <Excerpt
                    key={post.databaseId}
                    excerpt={post}
                    featured={index === 0}
                    category={getPostCategory(post)}
                  />
                ))}
          </Grid>
          <PaginationButtonContainer>
            {posts?.pageInfo.hasPreviousPage && (
              <IconButton
                aria-label={formatMessage(glossary.previous)}
                color="tertiary"
                icon={faChevronLeft}
                onClick={async () =>
                  await fetchMore({
                    variables: {
                      first: null,
                      after: null,
                      last: 13,
                      before: data.posts?.pageInfo.startCursor || null,
                    },
                    updateQuery,
                  })
                }
              />
            )}
            {posts?.pageInfo.hasNextPage && (
              <IconButton
                aria-label={formatMessage(glossary.next)}
                color="tertiary"
                icon={faChevronRight}
                onClick={async () =>
                  await fetchMore({
                    variables: {
                      first: 13,
                      after: data.posts?.pageInfo.endCursor || null,
                      last: null,
                      before: null,
                    },
                    updateQuery,
                  })
                }
              />
            )}
          </PaginationButtonContainer>
        </>
      ) : (
        <LoadingIndicator fullHeight />
      )}
    </Root>
  );
};
export default Home;
